import { useCallback, useEffect, useRef } from 'react';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { selectFeatureToggleState } from '../store/selectors';
import { graduate, setPreference } from '../store/slice';
import type { FeatureToggleConfig, UseFeatureToggleReturn } from '../types';

/**
 * Hook for managing feature toggle between versions
 *
 * Provides a simple two-way toggle between 'new' and 'classic' experiences.
 * Only available to users who pass the Statsig gate.
 * Preference is persisted per user via scoped storage.
 *
 * @param config - Configuration for the feature toggle
 * @returns Object with flags and handlers for managing the toggle
 */
export function useFeatureToggle(config: FeatureToggleConfig): UseFeatureToggleReturn {
  const dispatch = useAppDispatch();
  const gateEnabled = useFeatureGate(config.featureKey);
  const { toggle } = useEventTracking();
  const hasTrackedModeRef = useRef(false);

  // Get preference from Redux (user-scoped via scopedStorage)
  const toggleState = useAppSelector(state => selectFeatureToggleState(state, config.featureKey));

  const preference = toggleState?.preference ?? null;
  const isGraduated = toggleState?.isGraduated ?? false;

  // Gate is enabled - determine which experience to show
  // Default to 'new' if no preference set (gate enabled = default to new)
  const isOnNew = gateEnabled && (preference === 'new' || preference === null);

  // Track which mode the user is viewing on initial load
  useEffect(() => {
    if (gateEnabled && !hasTrackedModeRef.current) {
      hasTrackedModeRef.current = true;
      toggle.modeViewed({
        feature_key: config.featureKey,
        mode: isOnNew ? 'new' : 'classic',
      });
    }
  }, [gateEnabled, config.featureKey, isOnNew, toggle]);

  const switchToNew = useCallback(() => {
    dispatch(setPreference({ featureKey: config.featureKey, preference: 'new' }));
    toggle.switchedToNew({
      feature_key: config.featureKey,
    });
  }, [dispatch, config.featureKey, toggle]);

  const switchToClassic = useCallback(() => {
    dispatch(setPreference({ featureKey: config.featureKey, preference: 'classic' }));
    toggle.switchedToClassic({
      feature_key: config.featureKey,
    });
  }, [dispatch, config.featureKey, toggle]);

  const handleGraduate = useCallback(() => {
    dispatch(graduate({ featureKey: config.featureKey }));
    toggle.graduated({
      feature_key: config.featureKey,
    });
  }, [dispatch, config.featureKey, toggle]);

  // If gate is not enabled, user is not in the experiment
  // They get classic experience with no toggle option
  if (!gateEnabled) {
    return {
      showNew: false,
      showClassic: true,
      isOnNew: false,
      showBanner: false,
      switchToNew,
      switchToClassic,
      graduate: handleGraduate,
    };
  }

  // If gate is enabled and user is new (never saw classic), give them new experience directly
  // No toggle banner shown - they get the gate-controlled experience seamlessly
  if (config.isNewUser) {
    return {
      showNew: true,
      showClassic: false,
      isOnNew: true,
      showBanner: false, // No toggle for new users
      switchToNew,
      switchToClassic,
      graduate: handleGraduate,
    };
  }

  return {
    showNew: isOnNew,
    showClassic: !isOnNew,
    isOnNew,
    showBanner: config.showBanner !== false && !isGraduated, // Hide banner if graduated
    switchToNew,
    switchToClassic,
    graduate: handleGraduate,
  };
}
